// Helpers layered on top of `collectFieldStates` for callers that walk a
// form looking for questions that still need a user answer (e.g. a chat-based
// form-filler). Kept separate from `introspection.ts` because that module is
// the low-level "flatten every field" view and doesn't need to know about
// which types accept user input or what counts as an empty answer.

import { collectFieldStates, type FieldState } from './introspection'
import { richTextToText } from './richText'
import {
  type DynamicFormDefinition,
  type DynamicFormField,
  type DynamicFormValue,
} from './types'

/**
 * Field types that carry a user-provided value — the answerable ones. Excludes
 * containers (`group`), non-input types (`markup`, `hidden`), and any future
 * type that isn't a leaf answer node.
 */
export const ANSWERABLE_FIELD_TYPES: ReadonlySet<string> = new Set([
  'text',
  'textarea',
  'number',
  'date',
  'boolean',
  'radio',
  'select',
  'multi_select',
  'multi_checkbox',
  'scalar_list',
  'upload',
])

/**
 * A field the user can actually type into. `collectFieldStates`'s `visible` flag
 * answers "would this render", which is not the same question — a `display_only`
 * or `readonly` field renders but takes no answer.
 */
export function isEditableField(field: DynamicFormField): boolean {
  return (
    field.hidden !== true &&
    field.display_only !== true &&
    field.readonly !== true &&
    field.disabled !== true
  )
}

export function isEmptyValue(value: unknown): boolean {
  if (value === undefined || value === null) return true
  // Read as text: an emptied rich-text editor still holds `<p><br></p>`.
  if (typeof value === 'string') return richTextToText(value) === ''
  if (Array.isArray(value)) return value.length === 0
  return false
}

export interface UnansweredFieldOptions {
  /** When true, only fields with `required: true` are returned. Default: false. */
  onlyRequired?: boolean
}

/**
 * Every visible input field whose current value is empty, in definition order.
 * Group/markup/hidden nodes are filtered out. Pass `{onlyRequired: true}` to
 * limit the walk to required fields only.
 */
export function unansweredFieldStates(
  definition: DynamicFormDefinition,
  value: DynamicFormValue,
  options: UnansweredFieldOptions = {},
): FieldState[] {
  const onlyRequired = options.onlyRequired ?? false
  return collectFieldStates(definition, value).filter((state) => {
    if (!state.visible) return false
    if (!ANSWERABLE_FIELD_TYPES.has(state.type)) return false
    if (onlyRequired && !state.required) return false
    return isEmptyValue(state.value)
  })
}

/** How much of a form currently carries an answer. */
export interface FormCoverage {
  /** Visible answerable fields — the denominator. */
  total: number
  /** Of those, how many hold a non-empty value. */
  filled: number
  /** `filled / total`, or 1 when the form has no answerable fields at all. */
  ratio: number
  /** Labels of the still-empty fields, in definition order. */
  unfilledLabels: string[]
  /** The subset of `unfilledLabels` whose fields are required. */
  requiredUnfilledLabels: string[]
}

/**
 * Coverage over the fields a user can actually see and answer right now.
 * Visibility-gated fields whose controller is unanswered are excluded — they
 * are not yet part of the form the user is looking at, so counting them would
 * understate coverage. Non-editable fields are excluded too, which is where
 * this deliberately parts company with `unansweredFieldStates`: the walker asks
 * about them (long-standing behavior), but they can never count against a
 * "how much of this form is filled in" ratio.
 */
export function formCoverage(
  definition: DynamicFormDefinition,
  value: DynamicFormValue,
): FormCoverage {
  const answerable = collectFieldStates(definition, value).filter(
    (state) =>
      state.visible && ANSWERABLE_FIELD_TYPES.has(state.type) && isEditableField(state.field),
  )
  const unfilled = answerable.filter((state) => isEmptyValue(state.value))
  const total = answerable.length
  return {
    total,
    filled: total - unfilled.length,
    ratio: total === 0 ? 1 : (total - unfilled.length) / total,
    unfilledLabels: unfilled.map((state) => state.label ?? state.key),
    requiredUnfilledLabels: unfilled
      .filter((state) => state.required)
      .map((state) => state.label ?? state.key),
  }
}
